import { Component, computed, inject, input, output, signal } from '@angular/core';
import { LoginResponse } from 'angular-auth-oidc-client';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ApiManufacturer, ViewService } from 'api';
import { filter, firstValueFrom, startWith } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AskAnExpertComponent } from 'common/ask-an-expert/ask-an-expert.component';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatDivider } from '@angular/material/divider';
import { ScreenService } from 'common/common/screen.service';
import { NotificationComponent } from 'common/notification/notification.component';
import { MatSelect, MatOption } from '@angular/material/select';
import { TranslateService } from '@ngx-translate/core';
import { TranslationLanguageService } from './i18n/translation-language.service';
import { ButtonDirective } from 'common/button/button.directive';
import { IconDirective } from 'common/icon/icon.directive';

interface LanguageOption {
  readonly code: string;
  readonly label: string;
}

@Component({
  selector: 'comp-app-header',
  templateUrl: './app-header.component.html',
  styleUrls: ['./app-header.component.scss'],
  imports: [
    RouterLink,
    TranslateModule,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatDivider,
    MatSelect,
    MatOption,
    NotificationComponent,
    ButtonDirective,
    IconDirective,
  ],
})
export class AppHeaderComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  public readonly dialog = inject(MatDialog);
  protected readonly screenService = inject(ScreenService);
  private readonly viewService = inject(ViewService);
  private readonly translate = inject(TranslateService);
  private readonly translationLanguages = inject(TranslationLanguageService);

  public loginResponse = input.required<LoginResponse | null>();
  public manufacturer = input.required<ApiManufacturer | null>();
  public adminOrg = input.required<string>();

  public switchTenant = output<void>();
  public login = output<void>();
  public logout = output<void>();

  public activeTopLevelComponent = signal<string>('');
  protected readonly languageOptions = computed(() => this.buildLanguageOptions());
  protected readonly selectedLanguage = signal(
    this.translate.currentLang || this.languageOptions().at(0),
  );
  protected readonly showLanguageSelector = computed(() => this.languageOptions().length > 1);

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        startWith(null),
      )
      .subscribe(() => {
        this.setActiveTopLevelComponent(this.route);
      });
  }

  async askAnExpert(): Promise<void> {
    const requirementId = this.route.snapshot.queryParams['requirementId'];
    const currentRequirement = requirementId
      ? await firstValueFrom(this.viewService.getRequirement(requirementId))
      : null;
    this.dialog.open(AskAnExpertComponent, {
      data: {
        requirement: currentRequirement,
      },
    });
  }

  private setActiveTopLevelComponent(route: ActivatedRoute): void {
    const children = route.root.children;
    if (children.length > 0) {
      const child = children[0];
      if (child) {
        const routeUrl = child.snapshot.url.map((segment) => segment.path).join('/');
        this.activeTopLevelComponent.set(routeUrl);
      }
    }
  }

  protected openManufacturerDetails(): void {
    this.router.navigate(['/manufacturer']);
  }

  protected openAbout() {
    this.router.navigate(['/about']);
  }

  protected onPrivacyNoticeClick() {
    window.open(
      'https://www.ul.com/resources/online-policies/privacy-notice',
      '_blank',
      'noopener,noreferrer',
    );
  }

  protected async changeLanguage(language: string): Promise<void> {
    await firstValueFrom(this.translationLanguages.useLanguage(language));
    this.selectedLanguage.set(this.translate.currentLang || language);
  }

  private buildLanguageOptions(): LanguageOption[] {
    const available = this.translationLanguages.availableLanguages();
    const ordered = new Set<string>();
    const browserLanguages =
      typeof navigator !== 'undefined' && navigator.languages.length > 0
        ? navigator.languages
        : typeof navigator !== 'undefined'
          ? [navigator.language]
          : [];

    for (const browserLanguage of browserLanguages) {
      const normalized = browserLanguage.toLowerCase();
      const match =
        available.find((language) => language.toLowerCase() === normalized) ??
        available.find((language) => language.toLowerCase() === normalized.split('-')[0]);
      if (match) ordered.add(match);
    }
    for (const language of available) ordered.add(language);

    return [...ordered].map((code) => ({
      code,
      label: new Intl.DisplayNames([code], { type: 'language' }).of(code) ?? code,
    }));
  }
}
